import { normalizeRoutePath } from '~/utils/route'

interface AuthorArticlesResponse {
  data: Article[]
  meta?: {
    author?: ArticleAuthor | null
  }
  pagination?: {
    total?: number
    page?: number
    perPage?: number
    lastPage?: number
  }
  total?: number
  page?: number
  perPage?: number
}

export const useAuthorPage = async () => {
  const nuxtApp = useNuxtApp()
  const route = useRoute()
  const { t } = useI18n()
  const { getPath, getBreadcrumb, domain } = useNavigationMenu()
  const { getFacebookLink, getLineLink, getInstagramLink } = useSocialLink()

  const authorId = computed(() => String(route.params.authorId || route.params.categorySlug || ''))
  const routePathKey = normalizeRoutePath(String(route.path))
  const page = ref<number>(parseInt(route.query.page as string, 10) || 1)
  const perPage = ref<number>(5)
  const total = useState<number>(`author-total-${routePathKey}`, () => 0)
  const cachedAuthor = useState<ArticleAuthor | null>(`author-profile-${authorId.value}`, () => null)
  const $api = nuxtApp.$api as typeof $fetch

  const loadAuthorPage = async (currentPage: number) => {
    let response: AuthorArticlesResponse | null = null
    try {
      response = await $api<AuthorArticlesResponse>(`/articles/authors/${authorId.value}`, {
        query: {
          page: currentPage,
          perPage: perPage.value,
          populate: 'articleCategory,articleTags,author',
          orderBy: 'publishedAt',
          sortBy: 'desc',
        },
      })
    } catch {
      try {
        response = await $api<AuthorArticlesResponse>('/articles', {
          query: {
            page: 1,
            perPage: 1000,
            authorId: authorId.value,
            populate: 'articleCategory,articleTags,author',
            orderBy: 'publishedAt',
            sortBy: 'desc',
          },
        })
      } catch {
        response = null
      }
    }

    const rawArticles = response?.data || []
    const hasAuthorIdentity = rawArticles.some(article =>
      Boolean(article.authorId || article.author?._id || article.author?.id),
    )
    const filteredArticles = hasAuthorIdentity
      ? rawArticles.filter(article =>
          article.authorId === authorId.value ||
          article.author?._id === authorId.value ||
          article.author?.id === authorId.value,
        )
      : rawArticles
    const isFallbackListResponse = !response?.meta?.author && !response?.pagination
    const startIndex = (currentPage - 1) * perPage.value
    const authorArticles = isFallbackListResponse
      ? filteredArticles.slice(startIndex, startIndex + perPage.value)
      : filteredArticles

    return {
      author: response?.meta?.author || authorArticles[0]?.author || cachedAuthor.value || null,
      articles: authorArticles,
      total: response?.pagination?.total || response?.total || filteredArticles.length,
      perPage: response?.pagination?.perPage || response?.perPage || perPage.value,
    }
  }

  const { data: ssrData } = await useAsyncData(`author-page-${routePathKey}`, () => loadAuthorPage(page.value))

  const author = ref<ArticleAuthor | null>(ssrData.value?.author || null)
  const articles = ref<Article[]>(ssrData.value?.articles || [])
  total.value = ssrData.value?.total || 0
  perPage.value = ssrData.value?.perPage || perPage.value

  if (!authorId.value) {
    throw createError({ statusCode: 404, statusMessage: 'Page not found' })
  }

  if (import.meta.client) {
    watch(
      () => route.query.page,
      (newPage) => {
        const newPageValue = parseInt(newPage as string, 10) || 1
        if (page.value !== newPageValue) {
          page.value = newPageValue
        }
      },
      { immediate: true },
    )

    watch(page, async (newPage, oldPage) => {
      if (newPage === oldPage) return
      const response = await loadAuthorPage(newPage)
      author.value = response.author
      articles.value = response.articles
      total.value = response.total
      perPage.value = response.perPage
    })
  }

  const authorName = computed(() => author.value?.penName || author.value?.fullName || '')
  const breadcrumbs = computed(() => getBreadcrumb(t('global.writer')))
  const image = computed(() => author.value?.profileImage || null)
  const socialLinks = computed(() => [
    author.value?.facebookId
      ? { key: 'facebook', icon: 'fa-brands fa-square-facebook', href: getFacebookLink(author.value.facebookId) }
      : null,
    author.value?.lineId
      ? { key: 'line', icon: 'fa-brands fa-line', href: getLineLink(author.value.lineId) }
      : null,
    author.value?.instagramId
      ? { key: 'instagram', icon: 'fa-brands fa-square-instagram', href: getInstagramLink(author.value.instagramId) }
      : null,
    author.value?.youtubeLink
      ? { key: 'youtube', icon: 'fa-brands fa-youtube', href: author.value.youtubeLink }
      : null,
  ].filter(Boolean) as { key: string; icon: string; href: string }[])

  useCustomSeo({
    title: () => authorName.value,
    description: () => author.value?.bio || '',
    image: () => image.value?.large || image.value?.medium || '',
  })

  const goToPage = (pageNumber: number) => {
    page.value = pageNumber
    navigateTo({
      query: { ...route.query, page: pageNumber.toString() },
    })
  }
  const paginationPage = computed({
    get: () => page.value,
    set: (pageNumber: number) => {
      goToPage(pageNumber)
    },
  })

  return {
    author,
    authorName,
    image,
    socialLinks,
    articles,
    breadcrumbs,
    paginationPage,
    perPage,
    total,
    getPath,
    goToPage,
    domain,
  }
}
